import React, { useState } from 'react';
import { Download, FileText, Code, Image, FileJson, File, Video, Archive, FileSpreadsheet, Presentation } from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import JSZip from 'jszip';

const DownloadMenu = ({ messages, conversationTitle, onClose }) => {
  const [isExporting, setIsExporting] = useState(false);

  const exportAsPDF = async () => {
    setIsExporting(true);
    try {
      const pdf = new jsPDF();
      const pageHeight = pdf.internal.pageSize.height;
      const margin = 15;
      let yPosition = margin;

      // Title
      pdf.setFontSize(18);
      pdf.setFont(undefined, 'bold');
      pdf.text(conversationTitle, margin, yPosition);
      yPosition += 10;

      // Date
      pdf.setFontSize(10);
      pdf.setFont(undefined, 'normal');
      pdf.text(`Exported: ${new Date().toLocaleString()}`, margin, yPosition);
      yPosition += 15;

      // Messages
      pdf.setFontSize(11);
      messages.forEach((msg, idx) => {
        // Check if we need a new page
        if (yPosition > pageHeight - 20) {
          pdf.addPage();
          yPosition = margin;
        }

        // Role header
        pdf.setFont(undefined, 'bold');
        pdf.text(msg.role === 'user' ? 'You:' : 'AI:', margin, yPosition);
        yPosition += 7;

        // Content
        pdf.setFont(undefined, 'normal');
        const lines = pdf.splitTextToSize(msg.content, 180);
        lines.forEach(line => {
          if (yPosition > pageHeight - 20) {
            pdf.addPage();
            yPosition = margin;
          }
          pdf.text(line, margin, yPosition);
          yPosition += 7;
        });

        yPosition += 5; // Space between messages
      });

      pdf.save(`${conversationTitle}.pdf`);
    } catch (error) {
      console.error('PDF export failed:', error);
      alert('Failed to export PDF. Please try again.');
    } finally {
      setIsExporting(false);
      onClose();
    }
  };

  const exportAsMarkdown = () => {
    let markdown = `# ${conversationTitle}\n\n`;
    markdown += `**Exported:** ${new Date().toLocaleString()}\n\n---\n\n`;

    messages.forEach((msg, idx) => {
      markdown += `### ${msg.role === 'user' ? 'You' : 'AI'}\n\n`;
      markdown += `${msg.content}\n\n`;
      if (idx < messages.length - 1) markdown += '---\n\n';
    });

    downloadFile(markdown, `${conversationTitle}.md`, 'text/markdown');
    onClose();
  };

  const exportAsText = () => {
    let text = `${conversationTitle}\n`;
    text += `Exported: ${new Date().toLocaleString()}\n\n`;
    text += '='.repeat(50) + '\n\n';

    messages.forEach((msg, idx) => {
      text += `${msg.role === 'user' ? 'You' : 'AI'}:\n`;
      text += `${msg.content}\n\n`;
      text += '-'.repeat(50) + '\n\n';
    });

    downloadFile(text, `${conversationTitle}.txt`, 'text/plain');
    onClose();
  };

  const exportAsJSON = () => {
    const data = {
      title: conversationTitle,
      exportedAt: new Date().toISOString(),
      messageCount: messages.length,
      messages: messages.map(msg => ({
        role: msg.role,
        content: msg.content,
        timestamp: msg.timestamp
      }))
    };

    downloadFile(JSON.stringify(data, null, 2), `${conversationTitle}.json`, 'application/json');
    onClose();
  };

  const exportAsImage = async () => {
    setIsExporting(true);
    try {
      const messagesContainer = document.querySelector('.messages-container');
      if (!messagesContainer) {
        throw new Error('Messages container not found');
      }

      const canvas = await html2canvas(messagesContainer, {
        backgroundColor: '#1a1a1a',
        scale: 2,
        logging: false
      });

      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${conversationTitle}.png`;
        a.click();
        URL.revokeObjectURL(url);
      });
    } catch (error) {
      console.error('Image export failed:', error);
      alert('Failed to export as image. Please try again.');
    } finally {
      setIsExporting(false);
      onClose();
    }
  };

  const exportCodeOnly = () => {
    const codeBlocks = messages
      .filter(msg => msg.content.includes('```'))
      .map(msg => {
        const matches = msg.content.match(/```[\s\S]*?```/g);
        return matches ? matches.join('\n\n') : '';
      })
      .filter(Boolean);

    if (codeBlocks.length === 0) {
      alert('No code blocks found in this conversation.');
      return;
    }

    const code = codeBlocks.join('\n\n---\n\n');
    downloadFile(code, `${conversationTitle}-code.txt`, 'text/plain');
    onClose();
  };

  // Extract image URLs from messages
  const extractImageUrls = () => {
    const imageUrls = [];
    messages.forEach(msg => {
      // Look for image URLs in message content or metadata
      if (msg.imageUrl) {
        imageUrls.push(msg.imageUrl);
      }
      // Also check for markdown image syntax
      const markdownImages = msg.content.match(/!\[.*?\]\((.*?)\)/g);
      if (markdownImages) {
        markdownImages.forEach(match => {
          const url = match.match(/\((.*?)\)/)[1];
          if (url.match(/\.(png|jpg|jpeg|gif|webp)$/i)) {
            imageUrls.push(url);
          }
        });
      }
      // Check for direct image URLs
      const urlMatches = msg.content.match(/https?:\/\/[^\s]+\.(png|jpg|jpeg|gif|webp)/gi);
      if (urlMatches) {
        imageUrls.push(...urlMatches);
      }
    });
    return [...new Set(imageUrls)]; // Remove duplicates
  };

  // Extract video URLs from messages
  const extractVideoUrls = () => {
    const videoUrls = [];
    messages.forEach(msg => {
      // Look for video URLs in message content or metadata
      if (msg.videoUrl) {
        videoUrls.push(msg.videoUrl);
      }
      // Check for direct video URLs
      const urlMatches = msg.content.match(/https?:\/\/[^\s]+\.(mp4|webm|mov|avi)$/gi);
      if (urlMatches) {
        videoUrls.push(...urlMatches);
      }
    });
    return [...new Set(videoUrls)]; // Remove duplicates
  };

  const exportAllImages = async () => {
    setIsExporting(true);
    try {
      const imageUrls = extractImageUrls();
      
      if (imageUrls.length === 0) {
        alert('No images found in this conversation.');
        setIsExporting(false);
        return;
      }

      const zip = new JSZip();
      const folder = zip.folder('images');

      // Fetch and add each image to the ZIP
      for (let i = 0; i < imageUrls.length; i++) {
        try {
          const response = await fetch(imageUrls[i]);
          const blob = await response.blob();
          const extension = imageUrls[i].split('.').pop().split('?')[0] || 'png';
          folder.file(`image_${i + 1}.${extension}`, blob);
        } catch (error) {
          console.error(`Failed to fetch image ${i + 1}:`, error);
        }
      }

      // Generate and download ZIP
      const content = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(content);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${conversationTitle}-images.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Image export failed:', error);
      alert('Failed to export images. Please try again.');
    } finally {
      setIsExporting(false);
      onClose();
    }
  };

  const exportAllVideos = async () => {
    setIsExporting(true);
    try {
      const videoUrls = extractVideoUrls();
      
      if (videoUrls.length === 0) {
        alert('No videos found in this conversation.');
        setIsExporting(false);
        return;
      }

      const zip = new JSZip();
      const folder = zip.folder('videos');

      // Fetch and add each video to the ZIP
      for (let i = 0; i < videoUrls.length; i++) {
        try {
          const response = await fetch(videoUrls[i]);
          const blob = await response.blob();
          const extension = videoUrls[i].split('.').pop().split('?')[0] || 'mp4';
          folder.file(`video_${i + 1}.${extension}`, blob);
        } catch (error) {
          console.error(`Failed to fetch video ${i + 1}:`, error);
        }
      }

      // Generate and download ZIP
      const content = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(content);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${conversationTitle}-videos.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Video export failed:', error);
      alert('Failed to export videos. Please try again.');
    } finally {
      setIsExporting(false);
      onClose();
    }
  };

  const exportAllMedia = async () => {
    setIsExporting(true);
    try {
      const imageUrls = extractImageUrls();
      const videoUrls = extractVideoUrls();
      
      if (imageUrls.length === 0 && videoUrls.length === 0) {
        alert('No media files found in this conversation.');
        setIsExporting(false);
        return;
      }

      const zip = new JSZip();

      // Add images
      if (imageUrls.length > 0) {
        const imagesFolder = zip.folder('images');
        for (let i = 0; i < imageUrls.length; i++) {
          try {
            const response = await fetch(imageUrls[i]);
            const blob = await response.blob();
            const extension = imageUrls[i].split('.').pop().split('?')[0] || 'png';
            imagesFolder.file(`image_${i + 1}.${extension}`, blob);
          } catch (error) {
            console.error(`Failed to fetch image ${i + 1}:`, error);
          }
        }
      }

      // Add videos
      if (videoUrls.length > 0) {
        const videosFolder = zip.folder('videos');
        for (let i = 0; i < videoUrls.length; i++) {
          try {
            const response = await fetch(videoUrls[i]);
            const blob = await response.blob();
            const extension = videoUrls[i].split('.').pop().split('?')[0] || 'mp4';
            videosFolder.file(`video_${i + 1}.${extension}`, blob);
          } catch (error) {
            console.error(`Failed to fetch video ${i + 1}:`, error);
          }
        }
      }

      // Generate and download ZIP
      const content = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(content);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${conversationTitle}-media.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Media export failed:', error);
      alert('Failed to export media. Please try again.');
    } finally {
      setIsExporting(false);
      onClose();
    }
  };

  // Export document via backend API (Word, Excel, PowerPoint, PDF)
  const handleExportDocument = async (format) => {
    setIsExporting(true);
    try {
      // Format conversation text
      const conversationText = messages.map(msg => {
        const role = msg.role === 'user' ? 'You' : 'AI';
        return `${role}:\n${msg.content}`;
      }).join('\n\n---\n\n');

      const userHash = localStorage.getItem('qxai_user_hash');
      if (!userHash) {
        alert('Please log in to export documents.');
        setIsExporting(false);
        return;
      }

      const response = await fetch('https://vtaas-backend-220419304601.us-central1.run.app/api/qxai/generate-document', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Hash': userHash
        },
        body: JSON.stringify({
          type: format,
          content: conversationText,
          title: `${conversationTitle} - ${new Date().toLocaleDateString()}`
        })
      });

      const data = await response.json();
      
      if (data.success && data.url) {
        // Open the document URL in a new tab to download
        window.open(data.url, '_blank');
      } else {
        throw new Error(data.error || 'Failed to generate document');
      }
    } catch (error) {
      console.error(`${format} export failed:`, error);
      alert(`Failed to export as ${format.toUpperCase()}. Please try again.`);
    } finally {
      setIsExporting(false);
      onClose();
    }
  };

  const downloadFile = (content, filename, type) => {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportOptions = [
    {
      id: 'pdf',
      label: 'Export as PDF',
      icon: FileText,
      description: 'Professional document format',
      action: exportAsPDF
    },
    {
      id: 'word',
      label: 'Export as Word',
      icon: FileText,
      description: 'Microsoft Word document (.docx)',
      action: () => handleExportDocument('word')
    },
    {
      id: 'excel',
      label: 'Export as Excel',
      icon: FileSpreadsheet,
      description: 'Microsoft Excel spreadsheet (.xlsx)',
      action: () => handleExportDocument('excel')
    },
    {
      id: 'powerpoint',
      label: 'Export as PowerPoint',
      icon: Presentation,
      description: 'Microsoft PowerPoint presentation (.pptx)',
      action: () => handleExportDocument('pptx')
    },
    {
      id: 'markdown',
      label: 'Export as Markdown',
      icon: FileText,
      description: 'Formatted text with headers',
      action: exportAsMarkdown
    },
    {
      id: 'text',
      label: 'Export as Text',
      icon: File,
      description: 'Plain text format',
      action: exportAsText
    },
    {
      id: 'json',
      label: 'Export as JSON',
      icon: FileJson,
      description: 'Structured data with metadata',
      action: exportAsJSON
    },
    {
      id: 'image',
      label: 'Export as Image',
      icon: Image,
      description: 'Screenshot of conversation',
      action: exportAsImage
    },
    {
      id: 'code',
      label: 'Export Code Only',
      icon: Code,
      description: 'Extract code blocks',
      action: exportCodeOnly
    },
    {
      id: 'all-images',
      label: 'Export All Images',
      icon: Image,
      description: 'Download all PNG images as ZIP',
      action: exportAllImages
    },
    {
      id: 'all-videos',
      label: 'Export All Videos',
      icon: Video,
      description: 'Download all MP4 videos as ZIP',
      action: exportAllVideos
    },
    {
      id: 'all-media',
      label: 'Export All Media',
      icon: Archive,
      description: 'Download all images & videos as ZIP',
      action: exportAllMedia
    }
  ];

  return (
    <div className="vault-menu" style={{ minWidth: 280, overflow: 'hidden' }}>
      {/* Header */}
      <div
        className="vault-label"
        style={{ padding: '8px 10px 10px', marginBottom: 2, borderBottom: '1px solid var(--rule-soft)' }}
      >
        <Download size={14} />
        <span>Export Conversation</span>
      </div>

      {/* Options */}
      <div style={{ maxHeight: 400, overflowY: 'auto', paddingTop: 6 }}>
        {exportOptions.map(option => {
          const Icon = option.icon;
          return (
            <button
              key={option.id}
              className="vault-menu-item"
              style={{ alignItems: 'flex-start' }}
              onClick={option.action}
              disabled={isExporting}
            >
              <Icon size={15} style={{ flexShrink: 0, marginTop: 2 }} />
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontWeight: 500, color: 'var(--fg)', fontSize: 13 }}>
                  {option.label}
                </div>
                <div style={{ fontSize: 11, color: 'var(--fg-faint)', marginTop: 2 }}>
                  {option.description}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Loading */}
      {isExporting && (
        <div
          className="vault-label"
          style={{ justifyContent: 'center', padding: '12px 10px', marginBottom: 0, marginTop: 6, borderTop: '1px solid var(--rule-soft)' }}
        >
          <div
            className="animate-spin"
            style={{
              width: 14, height: 14, borderRadius: '50%',
              border: '2px solid var(--accent)', borderTopColor: 'transparent'
            }}
          ></div>
          <span>Exporting...</span>
        </div>
      )}
    </div>
  );
};

export default DownloadMenu;

